import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Progress } from '@heroui/react';
import { Icon } from '@iconify/react';

interface OptimizedRedirectComponentProps {
    /** 重定向路径 */
    redirectPath?: string;
    /** 重定向倒计时（秒） */
    redirectCountdown?: number;
    /** 取消重定向的回调函数 */
    onCancelRedirect?: () => void;
    /** 是否显示取消按钮 */
    showCancelButton?: boolean;
    /** 是否显示进度条 */
    showProgress?: boolean;
    /** 自定义标题 */
    title?: React.ReactNode;
    /** 自定义描述 */
    description?: React.ReactNode;
    /** 自定义图标 */
    icon?: string;
    /** 自定义样式类名 */
    className?: string;
}

export function OptimizedRedirectComponent({
    redirectPath,
    redirectCountdown = 3,
    onCancelRedirect,
    showCancelButton = true,
    showProgress = true,
    title,
    description,
    icon = "solar:shield-user-outline",
    className
}: OptimizedRedirectComponentProps): React.JSX.Element {
    const [countdown, setCountdown] = useState(redirectCountdown);
    const [progress, setProgress] = useState(0);
    const [isProcessing, setIsProcessing] = useState(true);
    const [showRetry, setShowRetry] = useState(false);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const progressRef = useRef<NodeJS.Timeout | null>(null);

    const clearTimers = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        if (progressRef.current) {
            clearInterval(progressRef.current);
            progressRef.current = null;
        }
    }, []);

    const handleCancel = useCallback(() => {
        clearTimers();
        setIsProcessing(false);
        onCancelRedirect?.();
    }, [clearTimers, onCancelRedirect]);

    const handleRetry = useCallback(() => {
        setCountdown(redirectCountdown);
        setProgress(0);
        setIsProcessing(true);
        setShowRetry(false);
    }, [redirectCountdown]);

    useEffect(() => {
        if (!isProcessing) return;

        setCountdown(redirectCountdown);
        setProgress(0);

        // 倒计时定时器
        const countdownTimer = setInterval(() => {
            setCountdown((prev: number) => {
                if (prev <= 1) {
                    clearTimers();
                    setIsProcessing(false);

                    // 执行重定向
                    window.location.href = redirectPath || '/';

                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        // 进度条定时器
        const progressTimer = setInterval(() => {
            setProgress(prev => {
                const newProgress = prev + (100 / redirectCountdown);
                return newProgress >= 100 ? 100 : newProgress;
            });
        }, 1000);

        timerRef.current = countdownTimer;
        progressRef.current = progressTimer;

        return () => {
            clearInterval(countdownTimer);
            clearInterval(progressTimer);
        };
    }, [isProcessing, redirectCountdown, redirectPath, clearTimers]);

    const getProgressColor = () => {
        if (progress < 30) return 'success';
        if (progress < 70) return 'warning';
        return 'danger';
    };

    const renderContent = () => {
        if (showRetry) {
            return (
                <div className="text-center space-y-4">
                    <Icon icon="solar:refresh-circle-line-duotone" className="w-16 h-16 mx-auto text-warning" />
                    <div>
                        <h2 className="text-lg font-semibold text-foreground mb-2">重定向超时</h2>
                        <p className="text-sm text-foreground-600 mb-4">
                            自动重定向似乎遇到了问题，您可以手动重试或直接访问目标页面。
                        </p>
                        <div className="flex gap-2 justify-center">
                            <Button
                                size="sm"
                                variant="flat"
                                onClick={handleRetry}
                                startContent={<Icon icon="solar:refresh-line-duotone" />}
                            >
                                重试
                            </Button>
                            <Button
                                size="sm"
                                color="primary"
                                onClick={() => window.location.href = redirectPath || '/'}
                                startContent={<Icon icon="solar:arrow-right-line-duotone" />}
                            >
                                立即前往
                            </Button>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="text-center space-y-4">
                <Icon icon={icon} className="w-16 h-16 mx-auto text-primary" />
                <div>
                    <h2 className="text-lg font-semibold text-foreground mb-2">
                        {title || '需要身份验证'}
                    </h2>
                    <p className="text-sm text-foreground-600 mb-4">
                        {description || '为了保护您的数据安全，需要先登录才能继续访问该页面'}
                    </p>

                    {isProcessing && (
                        <>
                            <div className="text-2xl font-bold text-primary mb-2">
                                {countdown}
                            </div>
                            <p className="text-xs text-foreground-500 mb-4">
                                {redirectCountdown - countdown + 1} 秒后自动跳转到登录页面
                            </p>

                            {showProgress && (
                                <Progress
                                    value={progress}
                                    color={getProgressColor()}
                                    className="mb-4"
                                    showValueLabel={false}
                                    size="sm"
                                />
                            )}
                        </>
                    )}

                    {showCancelButton && isProcessing && (
                        <div className="flex gap-2 justify-center">
                            <Button
                                size="sm"
                                variant="flat"
                                onClick={handleCancel}
                                startContent={<Icon icon="solar:close-line-duotone" />}
                            >
                                取消
                            </Button>
                            <Button
                                size="sm"
                                color="primary"
                                onClick={() => window.location.href = redirectPath || '/'}
                                startContent={<Icon icon="solar:arrow-right-line-duotone" />}
                            >
                                立即前往
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className={`min-h-screen flex items-center justify-center bg-background ${className || ''}`}>
            <div className="max-w-md w-full mx-4 p-6 bg-content1 rounded-lg shadow-lg border border-divider">
                {renderContent()}
            </div>
        </div>
    );
}